/**
 * DocumentDB Validation Schemas
 *
 * Validation schemas for document database management operations including
 * creation, updates, and access grants.
 */

import { z } from 'zod';
import { nonEmptyStringSchema, descriptionSchema, uuidSchema } from './common';

// ============================================================================
// DOCUMENTDB CODE VALIDATION
// ============================================================================

/**
 * DocumentDB code validation schema
 * Format: lowercase alphanumeric and hyphens only
 * Length: 3-50 characters
 * Examples: "user-profiles", "product-catalog", "order-history"
 */
export const documentDBCodeSchema = z
  .string({
    required_error: 'DocumentDB code is required',
    invalid_type_error: 'DocumentDB code must be a string',
  })
  .min(3, 'Code must be at least 3 characters long')
  .max(50, 'Code must not exceed 50 characters')
  .regex(
    /^[a-z0-9-]+$/,
    'Code must contain only lowercase letters, numbers, and hyphens'
  );

export type DocumentDBCodeValidation = z.infer<typeof documentDBCodeSchema>;

// ============================================================================
// DOCUMENTDB PERMISSION VALIDATION
// ============================================================================

/**
 * Valid DocumentDB permission values
 */
export const documentDBPermissionValues = [
  'documentdb:Connect',
  'documentdb:GetDocument',
  'documentdb:PutDocument',
  'documentdb:DeleteDocument',
  'documentdb:Query',
] as const;

/**
 * DocumentDB permission validation schema
 */
export const documentDBPermissionSchema = z.enum(documentDBPermissionValues, {
  required_error: 'Permission is required',
  invalid_type_error: 'Invalid permission value',
});

export type DocumentDBPermissionValidation = z.infer<typeof documentDBPermissionSchema>;

/**
 * DocumentDB permissions array validation schema
 */
export const documentDBPermissionsArraySchema = z
  .array(documentDBPermissionSchema)
  .min(1, 'At least one permission is required')
  .refine(
    (permissions) => new Set(permissions).size === permissions.length,
    'Duplicate permissions are not allowed'
  );

export type DocumentDBPermissionsArrayValidation = z.infer<typeof documentDBPermissionsArraySchema>;

// ============================================================================
// DOCUMENTDB CREATE VALIDATION
// ============================================================================

/**
 * DocumentDB creation schema
 * Used when creating a new document database for a tenant
 */
export const documentDBCreateSchema = z
  .object({
    documentDBCode: documentDBCodeSchema,
    documentDBName: nonEmptyStringSchema
      .min(1, 'DocumentDB name must be at least 1 character long')
      .max(100, 'DocumentDB name must not exceed 100 characters'),
    description: descriptionSchema,
    schemaVersion: z
      .string()
      .max(50, 'Schema version must not exceed 50 characters')
      .optional(),
    documentSchema: z
      .record(z.unknown())
      .optional(),
  })
  .strict();

export type DocumentDBCreateValidation = z.infer<typeof documentDBCreateSchema>;

/**
 * Form-specific DocumentDB creation schema
 */
export const createDocumentDBFormSchema = z
  .object({
    documentDBName: nonEmptyStringSchema
      .min(1, 'DocumentDB name is required')
      .max(100, 'DocumentDB name must not exceed 100 characters'),
    documentDBCode: documentDBCodeSchema,
    description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
    schemaVersion: z.string().max(50, 'Schema version must not exceed 50 characters').optional(),
  });

export type CreateDocumentDBFormData = z.infer<typeof createDocumentDBFormSchema>;

// ============================================================================
// DOCUMENTDB UPDATE VALIDATION
// ============================================================================

/**
 * DocumentDB update schema
 * All fields are optional (at least one must be provided)
 * Note: documentDBCode is immutable and cannot be updated
 */
export const documentDBUpdateSchema = z
  .object({
    documentDBName: nonEmptyStringSchema
      .min(1, 'DocumentDB name must be at least 1 character long')
      .max(100, 'DocumentDB name must not exceed 100 characters')
      .optional(),
    description: descriptionSchema.nullable(),
    schemaVersion: z
      .string()
      .max(50, 'Schema version must not exceed 50 characters')
      .optional()
      .nullable(),
    documentSchema: z
      .record(z.unknown())
      .optional()
      .nullable(),
  })
  .strict()
  .refine(
    (data) => Object.keys(data).length > 0,
    'At least one field must be provided for update'
  );

export type DocumentDBUpdateValidation = z.infer<typeof documentDBUpdateSchema>;

/**
 * Form-specific DocumentDB update schema
 */
export const updateDocumentDBFormSchema = z
  .object({
    documentDBName: nonEmptyStringSchema
      .min(1, 'DocumentDB name is required')
      .max(100, 'DocumentDB name must not exceed 100 characters'),
    description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
    schemaVersion: z.string().max(50, 'Schema version must not exceed 50 characters').optional(),
  });

export type UpdateDocumentDBFormData = z.infer<typeof updateDocumentDBFormSchema>;

// ============================================================================
// DOCUMENTDB ACCESS GRANT VALIDATION
// ============================================================================

/**
 * DocumentDB access grant schema
 * Used when granting an application access to a document database
 */
export const documentDBAccessGrantSchema = z
  .object({
    applicationId: uuidSchema,
    permissions: documentDBPermissionsArraySchema,
  })
  .strict();

export type DocumentDBAccessGrantValidation = z.infer<typeof documentDBAccessGrantSchema>;

/**
 * DocumentDB access revoke schema
 */
export const documentDBAccessRevokeSchema = z
  .object({
    applicationId: uuidSchema,
  })
  .strict();

export type DocumentDBAccessRevokeValidation = z.infer<typeof documentDBAccessRevokeSchema>;

// ============================================================================
// DOCUMENTDB QUERY VALIDATION
// ============================================================================

/**
 * DocumentDB list query parameters schema
 */
export const documentDBListQuerySchema = z
  .object({
    search: z
      .string({
        invalid_type_error: 'Search must be a string',
      })
      .min(1, 'Search term must be at least 1 character')
      .max(100, 'Search term must not exceed 100 characters')
      .optional(),
    limit: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().min(1).max(100))
      .optional(),
  })
  .strict();

export type DocumentDBListQueryValidation = z.infer<typeof documentDBListQuerySchema>;
